import React from 'react';
import { BarChart3 } from 'lucide-react';
import SystemHeader from '../SystemHeader';

/**
 * Summary screen shown after successfully saving a daily report.
 * Displays checklist progress, monthly stats, and navigation buttons.
 */
const DailyReportSummary = ({ summaryData, accent, config, userInfo, onLogout, totalPoints, onRefreshPoints, onBack, onEdit }) => {
  const fmtDate = (d) => { const p = d?.split('-'); return p ? `${p[2]}/${p[1]}` : ''; };
  const sDone = summaryData.items.filter(it => it.done).length;
  const sTotal = summaryData.items.length;
  const sPct = sTotal > 0 ? Math.round((sDone / sTotal) * 100) : 0;
  const statEntries = Object.entries(summaryData.stats);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="Mục Tiêu CV trong ngày" onRefreshPoints={onRefreshPoints} />
      <div className="flex-1 max-w-md w-full mx-auto px-4 py-3 flex flex-col gap-2.5">
        {/* Notice */}
        <div className="flex items-center gap-2 py-2 px-3 rounded-lg border-2 border-dashed" style={{ borderColor: '#d97706', background: '#fef9c3' }}>
          <span className="text-base">✅</span>
          <div>
            <span className="text-sm font-bold" style={{ color: '#92400e' }}>Đã lưu!</span>
            <span className="text-xs ml-2" style={{ color: '#a16207' }}>📸 Chụp màn hình gửi nhóm</span>
          </div>
        </div>
        {/* User info */}
        <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-gray-100">
          {summaryData.userAvatar && <img src={summaryData.userAvatar} alt="" className="w-10 h-10 rounded-full border-2 border-gray-200 object-cover flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-gray-800 truncate">{summaryData.userName}</div>
            <div className="text-[11px] text-gray-400">{fmtDate(summaryData.date)} • {summaryData.time}</div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-xl font-black" style={{ color: sPct === 100 ? '#16a34a' : accent }}>{sPct}%</div>
            <div className="text-[10px] text-gray-400">{sDone}/{sTotal}</div>
          </div>
        </div>
        {/* Checklist */}
        <div className="bg-white rounded-xl p-3 border border-gray-100">
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2.5">
            <div className="h-full rounded-full" style={{ width: `${sPct}%`, background: sPct === 100 ? '#16a34a' : accent }} />
          </div>
          <div className="space-y-1">
            {summaryData.items.map((it, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg" style={{ background: it.done ? '#f0fdf4' : '#fafafa' }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white flex-shrink-0" style={{ background: it.done ? '#16a34a' : '#9ca3af' }}>{i + 1}</span>
                {it.done && <span className="text-green-500 text-xs flex-shrink-0">✓</span>}
                <span className={`text-sm flex-1 ${it.done ? 'text-green-700 font-medium' : 'text-gray-600'}`}>{it.text}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Monthly stats */}
        {statEntries.length > 0 && (
          <div className="bg-white rounded-xl p-3 border border-gray-100">
            <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-wider"><BarChart3 size={11} /> Tháng {new Date().getMonth() + 1}</div>
            <div className={`grid gap-2 ${statEntries.length <= 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {summaryData.stats.reels != null && <div className="text-center py-1.5 rounded-lg" style={{ background: '#fdf2f8' }}><div className="text-lg font-black text-pink-600">{summaryData.stats.reels}</div><div className="text-[9px] font-semibold text-pink-400">Reels</div></div>}
              {summaryData.stats.market != null && <div className="text-center py-1.5 rounded-lg" style={{ background: '#f0fdf4' }}><div className="text-lg font-black text-green-600">{summaryData.stats.market}</div><div className="text-[9px] font-semibold text-green-400">Thị trường</div></div>}
              {summaryData.stats.livestream != null && <div className="text-center py-1.5 rounded-lg" style={{ background: '#faf5ff' }}><div className="text-lg font-black text-purple-600">{summaryData.stats.livestream}</div><div className="text-[9px] font-semibold text-purple-400">Livestream</div></div>}
              {summaryData.stats.inventory != null && <div className="text-center py-1.5 rounded-lg" style={{ background: '#fef3c7' }}><div className="text-lg font-black text-amber-600">{summaryData.stats.inventory}</div><div className="text-[9px] font-semibold text-amber-400">Kho</div></div>}
            </div>
          </div>
        )}
        {/* Action buttons */}
        <div className="flex gap-2">
          <button onClick={onBack} className="flex-1 py-3 rounded-xl text-white font-semibold text-sm shadow-md active:scale-[.97] transition-transform" style={{ background: accent }}>← Quay lại danh sách</button>
          <button onClick={onEdit} className="flex-1 py-3 rounded-xl font-semibold text-sm shadow-md active:scale-[.97] transition-transform border-2" style={{ color: accent, borderColor: accent, background: 'white' }}>✏️ Chỉnh sửa</button>
        </div>
      </div>
    </div>
  );
};

export default DailyReportSummary;
